import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

const base = (companyId) => `${companyId}/time-reporting`;

// ─── TIME ENTRIES ───

export const getTimeEntries = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/entries`), { params, withCredentials: true });

export const getTimeEntry = (companyId, entryId) =>
  axios.get(getApiUrl(`${base(companyId)}/entries/${entryId}`), { withCredentials: true });

// Batch fetch entries for many (employee, period) pairs in one round-trip.
// Used by ApprovalPage to avoid N+1 when a manager loads many pending periods.
// Each item is authorized independently — items the caller can't read return
// { error: 'forbidden' } in their slot.
export const getTimeEntriesBatch = (companyId, requests) =>
  axios.post(getApiUrl(`${base(companyId)}/entries/batch`), { requests }, { withCredentials: true });

export const createTimeEntry = (companyId, data) =>
  axios.post(getApiUrl(`${base(companyId)}/entries`), data, { withCredentials: true });

export const updateTimeEntry = (companyId, entryId, data) =>
  axios.put(getApiUrl(`${base(companyId)}/entries/${entryId}`), data, { withCredentials: true });

export const deleteTimeEntry = (companyId, entryId) =>
  axios.delete(getApiUrl(`${base(companyId)}/entries/${entryId}`), { withCredentials: true });

export const getLeaveTypes = (companyId) =>
  axios.get(getApiUrl(`${base(companyId)}/leave-types`), { withCredentials: true });

export const getHoursSummary = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/hours-summary`), { params, withCredentials: true });

export const getCompBalance = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/comp-balance`), { params, withCredentials: true });

// Vacation balance wrappers moved to payrollApi.js — semester is a payroll
// concept, not a time-reporting concept. Importers should update to
// `import * as payrollApi from '../payrollApi'` and call
// payrollApi.getVacationBalance / getVacationBalancesBulk / setVacationBalance.

// ─── TIMESHEET PERIODS ───

export const getTimesheetPeriods = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/periods`), { params, withCredentials: true });

export const getPendingPeriods = (companyId) =>
  axios.get(getApiUrl(`${base(companyId)}/periods/pending`), { withCredentials: true });

export const getTimesheetPeriod = (companyId, periodId) =>
  axios.get(getApiUrl(`${base(companyId)}/periods/${periodId}`), { withCredentials: true });

// Read-only lookup for the current week/month period. Returns
// { period: null } if no period row exists yet — does NOT create one.
// Use this on navigation; the period is created server-side as a side
// effect of the first time entry being saved.
export const findPeriod = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/periods/find`), { params, withCredentials: true });

export const getOrCreatePeriod = (companyId, data) =>
  axios.post(getApiUrl(`${base(companyId)}/periods`), data, { withCredentials: true });

export const submitPeriod = (companyId, periodId) =>
  axios.post(getApiUrl(`${base(companyId)}/periods/${periodId}/submit`), {}, { withCredentials: true });

export const withdrawPeriod = (companyId, periodId) =>
  axios.post(getApiUrl(`${base(companyId)}/periods/${periodId}/withdraw`), {}, { withCredentials: true });

export const approvePeriod = (companyId, periodId) =>
  axios.post(getApiUrl(`${base(companyId)}/periods/${periodId}/approve`), {}, { withCredentials: true });

export const rejectPeriod = (companyId, periodId, reason) =>
  axios.post(getApiUrl(`${base(companyId)}/periods/${periodId}/reject`), { reason }, { withCredentials: true });

export const recalculatePeriod = (companyId, periodId) =>
  axios.post(getApiUrl(`${base(companyId)}/periods/${periodId}/recalculate`), {}, { withCredentials: true });

// ─── TIMER ───
// Backed by the active_timer table (one row per employee). Stop creates a
// draft time_entry (kind=work, work_category=ordinary); user can edit it
// afterwards to re-classify as overtime/comp.

export const getActiveTimer = (companyId) =>
  axios.get(getApiUrl(`${base(companyId)}/timer/active`), { withCredentials: true });

export const startTimer = (companyId, data) =>
  axios.post(getApiUrl(`${base(companyId)}/timer/start`), data, { withCredentials: true });

export const stopTimer = (companyId) =>
  axios.post(getApiUrl(`${base(companyId)}/timer/stop`), {}, { withCredentials: true });

export const discardTimer = (companyId) =>
  axios.post(getApiUrl(`${base(companyId)}/timer/discard`), {}, { withCredentials: true });

export const switchTimer = (companyId, data) =>
  axios.post(getApiUrl(`${base(companyId)}/timer/switch`), data, { withCredentials: true });

// ─── BILLABLE DATA (consumed by invoice module) ───

export const getBillableCustomers = (companyId) =>
  axios.get(getApiUrl(`${base(companyId)}/billable-customers`), { withCredentials: true });

export const getBillableEntries = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/billable-entries`), { params, withCredentials: true });

export const markEntriesInvoiced = (companyId, entries) =>
  axios.post(getApiUrl(`${base(companyId)}/entries/mark-invoiced`), { entries }, { withCredentials: true });

export const markEntriesUninvoiced = (companyId, invoiceItemId) =>
  axios.post(getApiUrl(`${base(companyId)}/entries/mark-uninvoiced`), { invoiceItemId }, { withCredentials: true });

// ─── PAYROLL DATA (consumed by payroll module) ───

export const getPayrollReadiness = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/payroll-readiness`), { params, withCredentials: true });

export const getPayrollEntries = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/payroll-entries`), { params, withCredentials: true });

export const markEntriesPayrolled = (companyId, entries) =>
  axios.post(getApiUrl(`${base(companyId)}/entries/mark-payrolled`), { entries }, { withCredentials: true });

export const markEntriesUnpayrolled = (companyId, payrollId) =>
  axios.post(getApiUrl(`${base(companyId)}/entries/mark-unpayrolled`), { payrollId }, { withCredentials: true });

// ─── REPORTS ───

export const getUtilizationReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/utilization`), { params, withCredentials: true });

export const getDistributionReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/distribution`), { params, withCredentials: true });

// Per-employee summary — primary view for payroll/invoicing.
// params: { startDate, endDate, statusFilter?: 'all' | 'approved' }
export const getPerEmployeeReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/per-employee`), { params, withCredentials: true });

// Per-project rollup — primary view for invoicing.
export const getPerProjectReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/per-project`), { params, withCredentials: true });

// Per-customer rollup — projects nested under their customer.
export const getPerCustomerReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/per-customer`), { params, withCredentials: true });

// Team-wide summary with period-over-period delta. Powers the
// Sammanfattning dashboard tab.
export const getSummaryReport = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/summary`), { params, withCredentials: true });

// CSV (default) and PDF exports both go through the same endpoint —
// pass format='pdf' for PDF, omit (or 'csv') for CSV.
export const exportReportCsv = (companyId, params) =>
  axios.get(getApiUrl(`${base(companyId)}/reports/export`), { params, withCredentials: true, responseType: 'blob' });

// ─── RETENTION / GDPR (time_entry_history purge — admin only) ───

export const previewRetention = (companyId, retentionYears) =>
  axios.get(getApiUrl(`${base(companyId)}/retention/preview`), {
    params: retentionYears ? { retentionYears } : undefined,
    withCredentials: true,
  });

export const purgeRetention = (companyId, data) =>
  axios.post(getApiUrl(`${base(companyId)}/retention/purge`), data || {}, { withCredentials: true });

